import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import './CreditsCounter.css';

const CreditsCounter = ({
    isLoadingUsage: initialLoading,
    monthlyUsage: initialUsage,
    posthog,
    page = 'unknown',
    source = 'credits_card'
}) => {
    const { t } = useTranslation();
    const [monthlyUsage, setMonthlyUsage] = useState(initialUsage);
    const [isLoadingUsage, setIsLoadingUsage] = useState(initialLoading);
    
    useEffect(() => {
        setMonthlyUsage(initialUsage);
        setIsLoadingUsage(initialLoading);
    }, [initialUsage, initialLoading]);
    
    // Listen for real-time updates to user's credit data
    useEffect(() => {
        if (!auth.currentUser) return;
        
        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        let lastMonthlyGenerations = null;
        
        const unsubscribe = onSnapshot(userDocRef, async (docSnapshot) => {
            if (docSnapshot.exists()) {
                const userData = docSnapshot.data();
                const monthlyGenerations = userData.monthlyGenerations || {};
                
                // Check if monthlyGenerations has changed (count or earnedCredits)
                const hasChanged = !lastMonthlyGenerations || 
                    lastMonthlyGenerations.count !== monthlyGenerations.count ||
                    lastMonthlyGenerations.earnedCredits !== monthlyGenerations.earnedCredits ||
                    lastMonthlyGenerations.adminOverride !== monthlyGenerations.adminOverride;
                
                // Update lastMonthlyGenerations when there's a real change
                if (hasChanged) {
                    lastMonthlyGenerations = { ...monthlyGenerations };
                }
                
                // Always use cached credits if available
                if (userData.calculatedCredits) {
                    const cachedCredits = userData.calculatedCredits;
                    setMonthlyUsage({
                        currentCount: cachedCredits.currentCount || 0,
                        monthlyLimit: cachedCredits.monthlyLimit || 0,
                        bonusCredits: cachedCredits.bonusCredits || 0,
                        remaining: cachedCredits.remaining || 0,
                        month: null
                    });
                    setIsLoadingUsage(false);
                    
                    // If monthlyGenerations has changed, trigger a background recalculation
                    if (hasChanged) {
                        console.log('Credits changed, triggering recalculation...');
                        
                        // Call backend to recalculate (don't wait for it)
                        import("firebase/functions").then(async ({ httpsCallable }) => {
                            const { functions } = await import("../firebase");
                            const getUserMonthlyUsageFunc = httpsCallable(functions, 'getUserMonthlyUsage');
                            getUserMonthlyUsageFunc().catch(error => {
                                console.error('Error recalculating credits:', error);
                            });
                        });
                    }
                } else {
                    // No cached credits, call backend to calculate them
                    setIsLoadingUsage(true);
                    // Remember current state to avoid triggering again
                    lastMonthlyGenerations = { ...monthlyGenerations };
                    try {
                        const { httpsCallable } = await import("firebase/functions");
                        const { functions } = await import("../firebase");
                        const getUserMonthlyUsageFunc = httpsCallable(functions, 'getUserMonthlyUsage');
                        const result = await getUserMonthlyUsageFunc();
                        
                        if (result.data) {
                            setMonthlyUsage(result.data);
                            setIsLoadingUsage(false);
                        }
                    } catch (error) {
                        console.error('Error fetching monthly usage:', error);
                        // Fall back to basic calculation
                        const count = monthlyGenerations.count || 0;
                        const limit = 10; // Default fallback
                        setMonthlyUsage({
                            currentCount: count,
                            monthlyLimit: limit,
                            bonusCredits: 0,
                            remaining: Math.max(0, limit - count),
                            month: null
                        });
                        setIsLoadingUsage(false);
                    }
                }
            }
        }, (error) => {
            console.error('Error listening to user credits:', error);
            setIsLoadingUsage(false);
        });
        
        return () => unsubscribe();
    }, []);
    
    const handleGetMoreClick = () => {
        posthog?.capture('get_more_credits_clicked', {
            page: page,
            current_credits: monthlyUsage?.remaining,
            source: source
        });
    };

    return (
        <div className="maps-counter">
            <div className="maps-content">
                <span className="maps-badge">{t('credits.freeCredits')}</span>
                <div className="maps-count">
                    {isLoadingUsage ? (
                        <div className="loading-spinner-small" style={{ margin: '0 20px' }}></div>
                    ) : (
                        <>
                            <span className="current">{monthlyUsage?.remaining}</span>
                            <span className="separator">{t('credits.remaining')}</span>
                        </>
                    )}
                </div>
                <Link
                    to="/credits"
                    className="get-more-credits-btn"
                    onClick={handleGetMoreClick}
                >
                    {t('credits.getMore')}
                </Link>
            </div>
        </div>
    );
};

export default CreditsCounter; 